import { LoadingIndicator } from "@/app/(shop)/services/[slug]/components/loadingIndicator";
import { required } from "@/utils/inputValidators";
import { inputFieldTheme } from "@/utils/themes";
import React from "react";
import { ProfileIcon } from "../_icons/profileIcon";
import LocalizedPhoneInput from "../_localized/localizedPhoneInput";
import { ConfirmButton } from "../button/confirmButton";
import { TextButton } from "../button/textButton";
import { StyledInputField } from "../styledInputField/styledInputField";
import { isEmail } from "@/utils/inputValidators";
import { LoginFormFields } from "./loginInfoModal";
import { Merchant } from "@/dto/merchant";
import { UseFormReturn, UseFormSetValue } from "react-hook-form";
import CloseIcon from "@/components/_icons/closeIcon";

const perksEnabledLocales = ["en-US"];

export const LoginModalUsernameInput = ({
  merchant,
  form,
  setValue,
  phoneField,
  emailField,
  setErrorMessage,
  onRequestClose,
  merchantColor,
  showPhoneNumberInput,
  showEmailSwitching,
  setShowPhoneNumberInput,
  errorMessage,
  isLoading,
  isValid,
  setIsLoading,
  captchaRef,
  showGuestCheckout,
  handleGuestCheckout,
}: {
  merchant: Merchant;
  form: UseFormReturn<LoginFormFields>;
  setValue: UseFormSetValue<LoginFormFields>;
  phoneField: keyof Pick<LoginFormFields, "phone">;
  emailField: keyof Pick<LoginFormFields, "email">;
  setErrorMessage: (message: string | null) => void;
  onRequestClose: () => void;
  merchantColor: string;
  showPhoneNumberInput: boolean;
  showEmailSwitching: boolean;
  setShowPhoneNumberInput: (show: boolean) => void;
  errorMessage: string | null;
  isLoading: boolean;
  isValid: boolean;
  setIsLoading: (loading: boolean) => void;
  captchaRef: any;
  showGuestCheckout: boolean;
  handleGuestCheckout: () => void;
}) => {
  const perksEnabled = perksEnabledLocales.includes(merchant?.locale);
  return (
    <div className="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden p-4 [@media(max-height:480px)]:h-[100%] [@media(max-height:480px)]:overflow-auto">
      <div className="flex justify-between items-center px-6 pt-6">
        <button
          onClick={() => {
            setValue(phoneField, "");
            setValue(emailField, "");
            form.clearErrors();
            setErrorMessage(null);
            onRequestClose();
          }}
          className="text-gray-600 hover:text-gray-900 ml-auto"
          data-testid="login-modal-close-button"
        >
          <CloseIcon />
        </button>
      </div>
      <div className="px-4 pb-4 flex flex-col justify-center items-center max-h-[calc(100vh-6rem)]">
        <span className="shrink-0">
          <ProfileIcon brandingColor={merchantColor} />
        </span>
        <h1 className="mt-4 text-3xl font-medium text-center text-neutral-700 shrink">
          Sign up or log in
        </h1>
        <p className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 sm:w-[460px] shrink">
          {`Manage your orders` +
            (perksEnabled ? `, earn perks, and ` : ` and `) +
            `enjoy a faster checkout with an
          account.`}
        </p>
        <div className="my-5 w-full sm:w-1/2 flex flex-col items-center shrink">
          {/* TODO: ADD NEGATIVE FLOWS TO THESE INPUTS */}
          {showPhoneNumberInput && (
            <LocalizedPhoneInput
              name="phone"
              form={form}
              locale={merchant.locale}
            />
          )}
          {!showPhoneNumberInput && (
            <StyledInputField
              label="Email"
              name="email"
              form={form}
              validator={{
                isEmail: isEmail(),
                required: required("Email"),
              }}
              theme={inputFieldTheme}
            />
          )}
        </div>
        {showEmailSwitching && (
          <div>
            <p className="font-normal text-[15px] text-center">
              Sign up or log in with your{" "}
              <span
                className="font-semibold cursor-pointer"
                style={{ color: merchantColor }}
                onClick={() => setShowPhoneNumberInput(!showPhoneNumberInput)}
              >
                {showPhoneNumberInput ? "Email" : "Phone Number"}
              </span>{" "}
              instead
            </p>
          </div>
        )}
        {errorMessage && (
          <div className="text-red-600 text-sm mt-2 shrink">{errorMessage}</div>
        )}
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <ConfirmButton
            disabled={!isValid}
            style={{
              backgroundColor: !isValid ? "#C7C7C7" : merchantColor,
            }}
            className="px-16 py-5 mt-11 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] rounded-lg w-[250px] max-md:px-5 max-md:mt-10 shrink"
            onClick={() => {
              if (errorMessage) {
                setErrorMessage(null);
              }
              captchaRef.current?.reset();
              captchaRef.current?.execute();
            }}
          >
            <p>Continue</p>
          </ConfirmButton>
        )}
        {showGuestCheckout && (
          <TextButton
            className="mt-8 mb-6 text-xl font-medium leading-7 text-center max-md:mt-10"
            onClick={handleGuestCheckout}
            style={{ color: merchantColor }}
          >
            Check out as a guest
          </TextButton>
        )}
      </div>
    </div>
  );
};
